import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { generateArticle, fetchProviderStatus } from '../../services/aiGenerationService';

const {
  FiZap, FiCpu, FiKey, FiEye, FiEyeOff, FiSave, FiCopy, FiCheck,
  FiAlertTriangle, FiInfo, FiCode, FiLink, FiRefreshCw, FiDownload,
  FiEdit3, FiTag, FiFileText, FiChevronDown, FiChevronUp, FiX,
  FiBookOpen, FiShield, FiWifi, FiWifiOff, FiServer,
} = FiIcons;

// ── AI Model Registry (mirrors server/config/aiKeys.js) ──────────────────────
const AI_MODELS = [
  {
    id: 'anthropic', label: 'Claude (Anthropic)', badge: 'Recommended',
    badgeColor: 'bg-orange-100 text-orange-700',
    gradient:   'from-orange-500 to-amber-600',
    mcpSupported: true,
    models: [
      { id: 'claude-opus-4-5',            label: 'Claude Opus 4.5 (Most Capable)' },
      { id: 'claude-sonnet-4-5',          label: 'Claude Sonnet 4.5 (Balanced)'   },
      { id: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (Fast)'       },
      { id: 'claude-3-haiku-20240307',    label: 'Claude 3 Haiku (Lightweight)'   },
    ],
    docsUrl: 'https://console.anthropic.com/settings/keys',
    envKey:  'ANTHROPIC_API_KEY',
  },
  {
    id: 'openai', label: 'GPT (OpenAI)', badge: 'Popular',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    gradient:   'from-emerald-500 to-teal-600',
    mcpSupported: false,
    models: [
      { id: 'gpt-4o',        label: 'GPT-4o (Best Quality)'   },
      { id: 'gpt-4-turbo',   label: 'GPT-4 Turbo (Fast)'      },
      { id: 'gpt-4',         label: 'GPT-4 (Standard)'        },
      { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Economy)' },
    ],
    docsUrl: 'https://platform.openai.com/api-keys',
    envKey:  'OPENAI_API_KEY',
  },
  {
    id: 'deepseek', label: 'DeepSeek', badge: 'Cost Efficient',
    badgeColor: 'bg-blue-100 text-blue-700',
    gradient:   'from-blue-500 to-indigo-600',
    mcpSupported: false,
    models: [
      { id: 'deepseek-chat',     label: 'DeepSeek Chat (General)'  },
      { id: 'deepseek-reasoner', label: 'DeepSeek Reasoner (Logic)' },
    ],
    docsUrl: 'https://platform.deepseek.com/api_keys',
    envKey:  'DEEPSEEK_API_KEY',
  },
];

const TONES = [
  { id: 'compassionate', label: 'Compassionate' },
  { id: 'professional',  label: 'Professional'  },
  { id: 'inspirational', label: 'Inspirational' },
  { id: 'informative',   label: 'Informative'   },
  { id: 'celebratory',   label: 'Celebratory'   },
];

const LENGTHS = [
  { id: 'short',  label: 'Short (~300 words)',  tokens: 512  },
  { id: 'medium', label: 'Medium (~600 words)', tokens: 1024 },
  { id: 'long',   label: 'Long (~1200 words)',  tokens: 2048 },
];

const ARTICLE_TYPES = [
  { id: 'obituary',      label: 'Obituary'           },
  { id: 'tribute',       label: 'Legacy Tribute'     },
  { id: 'eulogy',        label: 'Eulogy'              },
  { id: 'memorial_blog', label: 'Memorial Blog Post' },
  { id: 'grief_guide',   label: 'Grief Support Guide'},
  { id: 'estate_guide',  label: 'Estate Planning'    },
];

const LS_PREFS_KEY = 'ofl_ai_article_prefs';
const loadPrefs    = () => { try { return JSON.parse(localStorage.getItem(LS_PREFS_KEY) || '{}'); } catch { return {}; } };

// ── Sub-components ────────────────────────────────────────────────────────────

const Toggle = ({ active, onToggle, label }) => (
  <button type="button" onClick={onToggle} className="flex items-center gap-3 w-full group" aria-pressed={active}>
    <div className={`w-11 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${active ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
      <motion.div
        animate={{ x: active ? 22 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
      />
    </div>
    {label && <span className="text-xs font-bold text-accent-navy group-hover:text-accent-gold transition-colors">{label}</span>}
  </button>
);

const Section = ({ icon, title, subtitle, children, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-[28px] border border-brand-dark overflow-hidden">
      <button
        type="button"
        onClick={() => collapsible && setOpen(o => !o)}
        className={`w-full flex items-center justify-between p-6 border-b border-brand-dark ${collapsible ? 'cursor-pointer hover:bg-brand-light/50 transition-colors' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-brand-light rounded-xl flex items-center justify-center">
            <SafeIcon icon={icon} className="w-4 h-4 text-accent-navy" />
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-accent-navy">{title}</p>
            {subtitle && <p className="text-[10px] text-text-muted font-light">{subtitle}</p>}
          </div>
        </div>
        {collapsible && <SafeIcon icon={open ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-text-muted" />}
      </button>
      <AnimatePresence initial={false}>
        {(!collapsible || open) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="p-6 space-y-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Provider Status Badge ─────────────────────────────────────────────────────
const ProviderStatusBadge = ({ status }) => {
  if (!status) return null;
  const configured = status.configured;
  return (
    <span className={`inline-flex items-center gap-1 text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest ${
      configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
    }`}>
      <SafeIcon icon={configured ? FiWifi : FiWifiOff} className="w-2.5 h-2.5" />
      {configured ? `Key: ${status.source}` : 'No Key'}
    </span>
  );
};

// ── MCP Config Panel ──────────────────────────────────────────────────────────
const MCPConfigPanel = ({ config, onChange }) => (
  <div className="space-y-4">
    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-start gap-3">
      <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-blue-700 leading-relaxed font-light">
        MCP (Model Context Protocol) allows Claude to call external tools — such as legacy databases and grief resource APIs — during generation. Tool execution happens server-side.
      </p>
    </div>
    {[
      { key: 'server_url',    label: 'MCP Server URL',         placeholder: 'https://mcp.your-server.com', icon: FiLink    },
      { key: 'tool_endpoint', label: 'Tool Endpoint Path',     placeholder: '/tools/content-lookup',       icon: FiCode    },
      { key: 'context_tool',  label: 'Context Injection Tool', placeholder: '/tools/legacy-context',       icon: FiCode    },
    ].map(f => (
      <div key={f.key}>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">{f.label}</label>
        <div className="relative">
          <SafeIcon icon={f.icon} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input
            type="text" value={config[f.key] || ''} placeholder={f.placeholder}
            onChange={e => onChange({ ...config, [f.key]: e.target.value })}
            className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono transition-colors"
          />
        </div>
      </div>
    ))}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
      {[
        { key: 'enable_tools',   label: 'Enable Tool Use'       },
        { key: 'inject_context', label: 'Inject Legacy Context' },
      ].map(t => (
        <div key={t.key} className="flex items-center p-3.5 bg-brand-light rounded-2xl border border-brand-dark">
          <Toggle active={!!config[t.key]} onToggle={() => onChange({ ...config, [t.key]: !config[t.key] })} label={t.label} />
        </div>
      ))}
    </div>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const AIArticleGenerator = () => {
  const prefs = loadPrefs();

  const [selectedProvider, setSelectedProvider] = useState(prefs.provider || 'anthropic');
  const [selectedModel,    setSelectedModel]    = useState(prefs.model    || 'claude-sonnet-4-5');
  const [mcpConfig,        setMcpConfig]        = useState(prefs.mcpConfig || { enable_tools: false, inject_context: false });
  const [providerStatus,   setProviderStatus]   = useState({});
  const [statusLoading,    setStatusLoading]    = useState(true);

  const [topic,              setTopic]              = useState('');
  const [keywordInput,       setKeywordInput]       = useState('');
  const [keywords,           setKeywords]           = useState([]);
  const [tone,               setTone]               = useState('compassionate');
  const [length,             setLength]             = useState('medium');
  const [articleType,        setArticleType]        = useState('tribute');
  const [customInstructions, setCustomInstructions] = useState('');

  const [output,       setOutput]       = useState('');
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [copied,       setCopied]       = useState(false);
  const [wordCount,    setWordCount]    = useState(0);
  const [editMode,     setEditMode]     = useState(false);
  const [editedOutput, setEditedOutput] = useState('');
  const [lastMeta,     setLastMeta]     = useState(null);
  const [prefsSaved,   setPrefsSaved]   = useState(false);

  const outputRef = useRef(null);
  const provider  = AI_MODELS.find(m => m.id === selectedProvider) || AI_MODELS[0];

  // ── Load provider status from backend ─────────────────────────────────────
  useEffect(() => {
    setStatusLoading(true);
    fetchProviderStatus()
      .then(s => setProviderStatus(s))
      .finally(() => setStatusLoading(false));
  }, []);

  // ── Provider change ───────────────────────────────────────────────────────
  const handleProviderChange = (pid) => {
    const p = AI_MODELS.find(m => m.id === pid);
    setSelectedProvider(pid);
    setSelectedModel(p?.models[0]?.id || '');
  };

  // ── Keywords ──────────────────────────────────────────────────────────────
  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !keywords.includes(kw) && keywords.length < 10) {
      setKeywords(prev => [...prev, kw]);
      setKeywordInput('');
    }
  };
  const removeKeyword = (kw) => setKeywords(prev => prev.filter(k => k !== kw));

  // ── Save UI preferences (NOT keys) ───────────────────────────────────────
  const handleSavePrefs = () => {
    localStorage.setItem(LS_PREFS_KEY, JSON.stringify({
      provider: selectedProvider, model: selectedModel, mcpConfig,
    }));
    setPrefsSaved(true);
    setTimeout(() => setPrefsSaved(false), 2000);
  };

  // ── Generate via backend ──────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!topic.trim()) { setError('Please enter a topic for the article.'); return; }

    const currentStatus = providerStatus[selectedProvider];
    if (!currentStatus?.configured) {
      setError(
        `No API key is configured for ${provider.label}. ` +
        `Please set ${provider.envKey} in the server .env file and restart the server.`
      );
      return;
    }

    setLoading(true);
    setError('');
    setOutput('');
    setEditMode(false);
    setLastMeta(null);

    try {
      const result = await generateArticle({
        provider:            selectedProvider,
        model:               selectedModel,
        topic,
        keywords,
        tone,
        length,
        article_type:        articleType,
        custom_instructions: customInstructions,
        mcp_config:          provider.mcpSupported ? mcpConfig : null,
      });

      setOutput(result.text);
      setEditedOutput(result.text);
      setWordCount(result.word_count);
      setLastMeta(result);
      setTimeout(() => outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      setError(err.message || 'Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [topic, keywords, tone, length, articleType, customInstructions, selectedProvider, selectedModel, provider, mcpConfig, providerStatus]);

  // ── Copy / Download ───────────────────────────────────────────────────────
  const handleCopy = async () => {
    await navigator.clipboard.writeText(editMode ? editedOutput : output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const text = editMode ? editedOutput : output;
    const blob = new Blob([text], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${topic.slice(0, 40).replace(/\s+/g, '-').toLowerCase()}-article.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentStatus = providerStatus[selectedProvider];
  const canGenerate   = topic.trim() && !loading && currentStatus?.configured;

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-accent-navy via-indigo-900 to-purple-900 text-white rounded-[32px] p-8 relative overflow-hidden shadow-2xl">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
        <div className="relative z-10 flex items-start gap-6">
          <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0">
            <SafeIcon icon={FiBookOpen} className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h2 className="font-serif text-2xl">AI Article Generator</h2>
              <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Server-Side Keys</span>
              <span className="text-[9px] px-3 py-1 bg-purple-500/30 text-purple-200 rounded-full font-black uppercase tracking-widest">MCP Enabled</span>
            </div>
            <p className="text-white/60 text-sm font-light max-w-xl">
              Generate compassionate legacy articles powered by Claude, GPT, or DeepSeek.
              API keys are stored securely on the server — never exposed to the browser.
            </p>
          </div>
          {/* Provider status pills */}
          <div className="hidden md:flex flex-col gap-2 flex-shrink-0">
            {AI_MODELS.map(p => {
              const s = providerStatus[p.id];
              return (
                <div key={p.id} className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2 border border-white/10">
                  <div className={`w-2 h-2 rounded-full ${s?.configured ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span className="text-[10px] text-white/70 font-medium">{p.label.split(' ')[0]}</span>
                  <span className={`text-[8px] font-black uppercase ${s?.configured ? 'text-green-300' : 'text-red-300'}`}>
                    {statusLoading ? '…' : s?.configured ? s.source : 'not set'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Server key notice ─────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-2xl">
        <SafeIcon icon={FiServer} className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700 font-light leading-relaxed">
          <strong className="font-bold">Secure key management:</strong> API keys are configured exclusively in{' '}
          <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-800">.env</code> on the server.
          Set <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-800">ANTHROPIC_API_KEY</code>,{' '}
          <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-800">OPENAI_API_KEY</code>, or{' '}
          <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-800">DEEPSEEK_API_KEY</code> and restart the server.
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* ── LEFT: Config ──────────────────────────────────────────────────── */}
        <div className="xl:col-span-2 space-y-5">
          <Section icon={FiCpu} title="AI Model Configuration" subtitle="Select provider and model">

            {/* Provider selector */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">AI Provider</label>
              <div className="grid grid-cols-1 gap-2">
                {AI_MODELS.map(p => {
                  const s = providerStatus[p.id];
                  return (
                    <button
                      key={p.id} type="button"
                      onClick={() => handleProviderChange(p.id)}
                      className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all ${
                        selectedProvider === p.id
                          ? 'border-accent-gold bg-accent-gold/5 shadow-md'
                          : 'border-brand-dark bg-brand-light hover:border-accent-navy/30'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${p.gradient}`}>
                        <SafeIcon icon={FiCpu} className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-bold text-accent-navy truncate">{p.label}</span>
                          <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-black uppercase ${p.badgeColor}`}>{p.badge}</span>
                          {p.mcpSupported && <span className="text-[8px] px-1.5 py-0.5 rounded-full font-black bg-purple-100 text-purple-700">MCP</span>}
                        </div>
                        <div className="mt-0.5">
                          <ProviderStatusBadge status={s} />
                        </div>
                      </div>
                      {selectedProvider === p.id && <SafeIcon icon={FiCheck} className="w-4 h-4 text-accent-gold flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Model version */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Model Version</label>
              <select
                value={selectedModel}
                onChange={e => setSelectedModel(e.target.value)}
                className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium text-accent-navy"
              >
                {provider.models.map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Key status for selected provider */}
            {currentStatus && (
              <div className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 ${
                currentStatus.configured
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <SafeIcon
                  icon={currentStatus.configured ? FiShield : FiAlertTriangle}
                  className={`w-4 h-4 flex-shrink-0 ${currentStatus.configured ? 'text-green-600' : 'text-red-500'}`}
                />
                <div>
                  <p className={`text-[10px] font-black uppercase tracking-widest ${currentStatus.configured ? 'text-green-700' : 'text-red-700'}`}>
                    {currentStatus.configured ? `Key configured (${currentStatus.source})` : 'No API key configured'}
                  </p>
                  {!currentStatus.configured && (
                    <p className="text-[9px] text-red-600 mt-0.5">
                      Set <code className="bg-red-100 px-1 rounded font-mono">{provider.envKey}</code> in server .env
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Save preferences */}
            <button
              type="button" onClick={handleSavePrefs}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                prefsSaved ? 'bg-green-500 text-white' : 'bg-brand-light border-2 border-brand-dark text-accent-navy hover:border-accent-gold'
              }`}
            >
              {prefsSaved
                ? <><SafeIcon icon={FiCheck} className="w-4 h-4" /> Preferences Saved!</>
                : <><SafeIcon icon={FiSave} className="w-4 h-4" /> Save Preferences</>}
            </button>
          </Section>

          {/* MCP Config — Claude only */}
          {provider.mcpSupported && (
            <Section icon={FiCode} title="MCP Server Integration" subtitle="Claude tool-use (server-side)" collapsible defaultOpen={false}>
              <MCPConfigPanel config={mcpConfig} onChange={setMcpConfig} />
            </Section>
          )}
        </div>

        {/* ── RIGHT: Article inputs + output ────────────────────────────────── */}
        <div className="xl:col-span-3 space-y-5">
          <Section icon={FiFileText} title="Article Parameters" subtitle="Define the content to generate">

            {/* Topic */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">
                Topic / Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text" value={topic} onChange={e => setTopic(e.target.value)}
                placeholder="e.g. Celebrating the life of Eleanor Grace Miller, a devoted teacher"
                className="w-full px-4 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/50 transition-colors"
              />
            </div>

            {/* Article Type */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Article Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ARTICLE_TYPES.map(t => (
                  <button key={t.id} type="button" onClick={() => setArticleType(t.id)}
                    className={`py-2.5 px-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
                      articleType === t.id ? 'bg-accent-navy border-accent-navy text-white' : 'bg-brand-light border-brand-dark text-text-muted hover:border-accent-gold'
                    }`}
                  >{t.label}</button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONES.map(t => (
                  <button key={t.id} type="button" onClick={() => setTone(t.id)}
                    className={`py-2 px-4 rounded-full text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
                      tone === t.id ? 'bg-accent-gold border-accent-gold text-white shadow-md' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                    }`}
                  >{t.label}</button>
                ))}
              </div>
            </div>

            {/* Length */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Article Length</label>
              <div className="grid grid-cols-3 gap-2">
                {LENGTHS.map(l => (
                  <button key={l.id} type="button" onClick={() => setLength(l.id)}
                    className={`py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
                      length === l.id ? 'bg-accent-navy border-accent-navy text-white' : 'bg-brand-light border-brand-dark text-text-muted hover:border-accent-gold'
                    }`}
                  >{l.label}</button>
                ))}
              </div>
            </div>

            {/* Keywords */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">
                Keywords <span className="text-text-muted font-light normal-case">(optional, up to 10)</span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <SafeIcon icon={FiTag} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="text" value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="Type a keyword and press Enter"
                    className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm transition-colors"
                  />
                </div>
                <button type="button" onClick={addKeyword}
                  className="px-4 py-3 bg-accent-navy text-white rounded-2xl hover:bg-accent-gold transition-all text-xs font-black"
                >Add</button>
              </div>
              {keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {keywords.map(kw => (
                    <span key={kw} className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-navy/10 text-accent-navy rounded-full text-[10px] font-bold">
                      {kw}
                      <button type="button" onClick={() => removeKeyword(kw)} className="hover:text-red-500 transition-colors">
                        <SafeIcon icon={FiX} className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Instructions */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">
                Custom Instructions <span className="text-text-muted font-light normal-case">(optional)</span>
              </label>
              <textarea
                value={customInstructions} onChange={e => setCustomInstructions(e.target.value)} rows={3}
                placeholder="e.g. Include a reference to her love of gardening and volunteering at the local hospice..."
                className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm resize-none placeholder:text-text-muted/50 transition-colors"
              />
            </div>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl"
                >
                  <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Generate */}
            <button
              type="button" onClick={handleGenerate} disabled={!canGenerate}
              className="w-full flex items-center justify-center gap-3 py-4 bg-accent-navy text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-accent-gold transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Generating Article…</>
              ) : (
                <><SafeIcon icon={FiZap} className="w-5 h-5" /> Generate Draft</>
              )}
            </button>
          </Section>

          {/* ── Output Panel ──────────────────────────────────────────────────── */}
          <AnimatePresence>
            {(output || loading) && (
              <motion.div ref={outputRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="bg-white rounded-[28px] border border-brand-dark overflow-hidden"
              >
                {/* Output header */}
                <div className="flex items-center justify-between p-5 border-b border-brand-dark bg-brand-light/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-accent-gold/20 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiFileText} className="w-4 h-4 text-accent-gold" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-accent-navy">Generated Article</p>
                      {wordCount > 0 && (
                        <p className="text-[10px] text-text-muted">
                          ~{wordCount} words
                          {lastMeta?.duration_ms && ` · ${(lastMeta.duration_ms / 1000).toFixed(1)}s`}
                          {lastMeta?.mcp_rounds  != null && lastMeta.mcp_rounds > 0 && ` · ${lastMeta.mcp_rounds} MCP round(s)`}
                        </p>
                      )}
                    </div>
                  </div>
                  {output && (
                    <div className="flex items-center gap-2">
                      <button type="button"
                        onClick={() => { setEditMode(v => !v); setEditedOutput(output); }}
                        className={`p-2.5 rounded-xl border-2 transition-all text-[10px] font-black flex items-center gap-1.5 ${
                          editMode ? 'bg-accent-navy border-accent-navy text-white' : 'border-brand-dark text-text-muted hover:border-accent-gold'
                        }`}
                      >
                        <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
                        {editMode ? 'Editing' : 'Edit'}
                      </button>
                      <button type="button" onClick={handleCopy}
                        className="p-2.5 rounded-xl border-2 border-brand-dark text-text-muted hover:border-accent-gold transition-all"
                        title="Copy to clipboard"
                      >
                        <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-3.5 h-3.5 ${copied ? 'text-green-500' : ''}`} />
                      </button>
                      <button type="button" onClick={handleDownload}
                        className="p-2.5 rounded-xl border-2 border-brand-dark text-text-muted hover:border-accent-gold transition-all"
                        title="Download as .txt"
                      >
                        <SafeIcon icon={FiDownload} className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={handleGenerate} disabled={loading}
                        className="p-2.5 rounded-xl border-2 border-brand-dark text-text-muted hover:border-accent-gold transition-all disabled:opacity-40"
                        title="Regenerate"
                      >
                        <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Output body */}
                <div className="p-6">
                  {loading && !output ? (
                    <div className="space-y-3 animate-pulse">
                      {[100, 90, 95, 80, 85, 70, 88].map((w, i) => (
                        <div key={i} className="h-4 bg-brand-light rounded-full" style={{ width: `${w}%` }} />
                      ))}
                      <div className="pt-4 space-y-2">
                        {[92, 88, 75].map((w, i) => (
                          <div key={i} className="h-4 bg-brand-light rounded-full" style={{ width: `${w}%` }} />
                        ))}
                      </div>
                    </div>
                  ) : editMode ? (
                    <textarea
                      value={editedOutput}
                      onChange={e => { setEditedOutput(e.target.value); setWordCount(e.target.value.trim().split(/\s+/).filter(Boolean).length); }}
                      rows={20}
                      className="w-full px-4 py-4 bg-brand-light border-2 border-accent-gold rounded-2xl outline-none text-sm text-accent-navy font-light leading-relaxed resize-y font-mono"
                    />
                  ) : (
                    <div className="prose prose-sm max-w-none text-text-muted leading-relaxed whitespace-pre-wrap font-light text-sm">
                      {output}
                    </div>
                  )}
                </div>

                {/* Output footer */}
                {output && (
                  <div className="px-6 pb-5 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2 text-[10px] text-text-muted flex-wrap">
                      <SafeIcon icon={FiCpu} className="w-3 h-3" />
                      <span>Generated by <strong className="text-accent-navy">{lastMeta?.model || selectedModel}</strong></span>
                      {lastMeta?.mcp_rounds != null && lastMeta.mcp_rounds > 0 && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-bold text-[8px] uppercase tracking-widest">
                          MCP: {lastMeta.mcp_rounds} round(s)
                        </span>
                      )}
                      {lastMeta?.usage?.input_tokens && (
                        <span className="text-[9px] text-text-muted">
                          {lastMeta.usage.input_tokens + (lastMeta.usage.output_tokens || 0)} tokens used
                        </span>
                      )}
                    </div>
                    {editMode && (
                      <button type="button"
                        onClick={() => { setOutput(editedOutput); setEditMode(false); }}
                        className="flex items-center gap-2 px-4 py-2 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all"
                      >
                        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> Save Edits
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AIArticleGenerator;